import { createFileRoute, Link, useRouter } from "@tanstack/react-router"
import { SearchX } from "lucide-react"
import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react"

import type { MemberRosterState } from "@/core/activity-points/types"
import type {
  ActivityPointsMonth,
  ActivityPointsRow,
} from "@/lib/activity-points-api"

import { ActivityMemberPicker } from "@/components/activity-levels/member-picker"
import {
  currentMonth,
  formatPeriod,
  MONTH_NAMES,
  MonthPicker,
  parsePeriod,
} from "@/components/members/month-picker"
import { EmptyState } from "@/components/shared/empty-state"
import { FilterSelect } from "@/components/shared/filter-select"
import { StatusAlert } from "@/components/shared/status-alert"
import { Button } from "@/components/ui/button"
import {
  Card,
  CardAction,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { toast } from "@/components/ui/toast"
import {
  GUILD_RANK_CATALOG,
  GUILD_RANK_LABEL,
  GUILD_RANK_VALUES,
  GUILD_SPECIAL_TITLE_CATALOG,
  GUILD_SPECIAL_TITLE_LABEL,
  GUILD_SPECIAL_TITLE_VALUES,
  type GuildRankTitle,
  type GuildSpecialTitle,
} from "@/core/guild-members/titles"
import {
  useActivityPointsMonthQuery,
  useSaveActivityPointsMonthMutation,
} from "@/hooks/activity-points"
import { formatGuildJoinDate } from "@/lib/guild-join-date"
import {
  type MemberId,
  type MembersRosterState,
  type ReportingPeriod,
  useMembersFocusIntent,
  useMembersRosterState,
  useMembersRouteStore,
} from "@/lib/stores/members-ui-store"

const MAX_INTEGER = 2_147_483_647
const VALIDATION_ERROR_ID = "activity-points-validation-error"
const DISCORD_PRESENCE_LABEL = {
  present: "Present",
  departed: "Left or removed",
  unknown: "Unknown",
} satisfies Record<ActivityPointsRow["discordPresence"], string>

type MembersSearch = {
  month?: string
  q?: string
  state: MemberRosterState
  title?: "none" | `rank:${GuildRankTitle}` | `special:${GuildSpecialTitle}`
}

function compactMembersSearch(search: Record<string, unknown>): MembersSearch {
  const month = typeof search.month === "string" ? search.month : undefined
  const q =
    typeof search.q === "string" ? search.q.trim() || undefined : undefined
  const state =
    search.state === "departed" ||
    search.state === "all" ||
    search.state === "present" ||
    search.state === "unknown"
      ? search.state
      : "active"
  let title: MembersSearch["title"]
  if (search.title === "none") title = "none"
  else if (typeof search.title === "string") {
    if (
      search.title.startsWith("rank:") &&
      GUILD_RANK_VALUES.includes(search.title.slice(5) as GuildRankTitle)
    ) {
      title = search.title as `rank:${GuildRankTitle}`
    } else if (
      search.title.startsWith("special:") &&
      GUILD_SPECIAL_TITLE_VALUES.includes(
        search.title.slice(8) as GuildSpecialTitle,
      )
    ) {
      title = search.title as `special:${GuildSpecialTitle}`
    }
  }
  return { month, state, ...(q ? { q } : {}), ...(title ? { title } : {}) }
}

export const Route = createFileRoute("/_authed/_admin/members/")({
  validateSearch: compactMembersSearch,
  component: ActivityPointsPage,
})

function ActivityPointsPage() {
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const router = useRouter()
  const parsed = parsePeriod(search.month)
  const selectedPeriod = parsed?.period ?? currentMonth()
  const { year, month } = parsePeriod(selectedPeriod)!
  const nameQuery = search.q ?? ""
  const rosterState = search.state
  const title = search.title ?? "all"
  const [nameDraft, setNameDraft] = useState(nameQuery)
  const nameId = useId()
  const nameInputRef = useRef<HTMLInputElement>(null)
  const membersStore = useMembersRouteStore()
  const state = useMembersRosterState(membersStore, selectedPeriod)
  const focusIntent = useMembersFocusIntent(membersStore)
  const mountedRef = useRef(true)
  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])
  const editButtonRefs = useRef<
    Record<ReportingPeriod, HTMLButtonElement | null>
  >({})
  const inputRefs = useRef<Record<MemberId, HTMLInputElement | null>>({})
  const originalPoints = useRef<
    Record<ReportingPeriod, Record<MemberId, string>>
  >({})
  const query = useActivityPointsMonthQuery(year, month, rosterState)
  const saveMutation = useSaveActivityPointsMonthMutation()
  const matchesSelection =
    query.data?.year === year && query.data.month === month
  const { actions } = membersStore
  useEffect(() => {
    if (state.mode !== "editing" || !matchesSelection || !query.data) return
    const originals = originalPoints.current[selectedPeriod]
    if (!originals) return
    for (const row of query.data.rows) {
      originals[row.memberId] ??= row.points === null ? "" : String(row.points)
    }
  }, [state.mode, matchesSelection, query.data, selectedPeriod])

  const updateFilters = useCallback(
    (updates: Partial<MembersSearch>, options: { replace: boolean }) => {
      const nextMonth = updates.month ?? selectedPeriod
      void navigate({
        search: compactMembersSearch({
          ...search,
          ...updates,
          month: nextMonth,
        }),
        replace: options.replace,
        resetScroll: false,
      })
    },
    [
      navigate,
      search.month,
      search.q,
      search.state,
      search.title,
      selectedPeriod,
    ],
  )

  useEffect(() => {
    void navigate({
      search: compactMembersSearch({ ...search, month: selectedPeriod }),
      replace: true,
      resetScroll: false,
    })
  }, [
    navigate,
    search.month,
    search.q,
    search.state,
    search.title,
    selectedPeriod,
  ])

  useEffect(() => setNameDraft(nameQuery), [nameQuery])

  useEffect(() => {
    const trimmed = nameDraft.trim()
    if (trimmed === nameQuery) return
    const timer = setTimeout(
      () => updateFilters({ q: trimmed || undefined }, { replace: true }),
      250,
    )
    return () => clearTimeout(timer)
  }, [nameDraft, nameQuery, updateFilters])

  useEffect(() => {
    if (
      !focusIntent ||
      focusIntent.area !== "roster" ||
      focusIntent.key !== selectedPeriod
    )
      return
    if (focusIntent.target === "edit" && query.isFetching) return
    const target =
      focusIntent.target === "edit"
        ? (editButtonRefs.current[focusIntent.key] ??
          (query.data?.rows.length === 0 ? nameInputRef.current : null))
        : inputRefs.current[focusIntent.target]
    if (!target || target.disabled) return
    target.focus()
    actions.consumeFocusIntent(focusIntent)
  }, [
    actions,
    focusIntent,
    selectedPeriod,
    state.mode,
    state.invalidMemberId,
    state.pending,
    nameQuery,
    rosterState,
    title,
    query.data,
    query.isFetching,
  ])

  const selectPeriod = (period: ReportingPeriod) =>
    updateFilters({ month: period }, { replace: false })
  const filtersActive = Boolean(
    search.q || rosterState !== "active" || search.title,
  )
  const clearFilters = useCallback(() => {
    setNameDraft("")
    updateFilters(
      { q: undefined, state: "active", title: undefined },
      { replace: false },
    )
  }, [updateFilters])

  const filteredRows = useMemo(() => {
    if (!query.data) return []
    const normalizedQuery = nameQuery.trim().toLowerCase()
    return query.data.rows.filter((row) => {
      if (
        normalizedQuery &&
        !row.displayName.toLowerCase().includes(normalizedQuery)
      )
        return false
      if (title === "none")
        return row.rankTitle === null && row.specialTitles.length === 0
      if (title.startsWith("rank:")) return row.rankTitle === title.slice(5)
      if (title.startsWith("special:"))
        return row.specialTitles.includes(title.slice(8) as GuildSpecialTitle)
      return true
    })
  }, [query.data, nameQuery, title])

  const save = async (
    result: ActivityPointsMonth,
    submitted: MembersRosterState,
  ) => {
    const period = formatPeriod(result.year, result.month)
    const entries: Array<{ memberId: MemberId; points: number }> = []
    for (const [memberId, value] of Object.entries(submitted.draft)) {
      if (value === "") continue
      const points = Number(value)
      if (!Number.isInteger(points) || points < 0 || points > MAX_INTEGER) {
        if (
          !result.rows.some((row) => row.memberId === memberId) ||
          nameQuery ||
          title !== "all"
        ) {
          updateFilters(
            { q: undefined, state: "all", title: undefined },
            { replace: true },
          )
        }
        actions.rejectRoster(period, submitted, memberId)
        return
      }
      if (value !== originalPoints.current[period]?.[memberId])
        entries.push({ memberId, points })
    }
    actions.beginRosterSave(period, submitted)
    try {
      await saveMutation.mutateAsync({
        year: result.year,
        month: result.month,
        state: rosterState,
        entries,
      })
      actions.completeRosterSave(period)
      if (
        mountedRef.current &&
        router.state.location.pathname === "/members" &&
        (parsePeriod(router.state.location.search.month)?.period ??
          currentMonth()) === period
      ) {
        toast.success("Activity points saved.")
      }
    } catch {
      actions.failRosterSave(period, submitted)
    }
  }

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Members</h1>
          <p className="text-sm text-muted-foreground">
            Review member profiles, titles, and monthly points.
          </p>
        </div>
        <Button
          render={<Link to="/members/title-suggestions" />}
          variant="outline"
        >
          Rank title suggestions
        </Button>
      </div>
      <Card>
        <CardContent className="grid gap-4 md:grid-cols-[auto_minmax(0,1fr)] md:items-start">
          <div className="grid w-full gap-1.5 md:w-auto [&_[data-slot=button]]:h-11 [&_[data-slot=button]]:w-full">
            <p className="text-sm leading-none font-medium">Reporting month</p>
            <MonthPicker
              year={year}
              month={month}
              disabled={state.mode === "editing" || state.pending}
              onSelect={selectPeriod}
            />
          </div>
          <div className="min-w-0 [&_[data-slot=button]]:h-11 [&_[data-slot=input]]:w-auto [&_[data-slot=input]]:flex-1">
            <ActivityMemberPicker
              id="member-profile-search"
              label="Find member"
              selectLabel="Open first result"
              onSelect={(member) =>
                void navigate({
                  to: "/members/$memberId",
                  params: { memberId: member.memberId },
                  search: { month: selectedPeriod, state: rosterState },
                })
              }
            />
          </div>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="grid min-w-0 gap-3 pt-5 md:grid-cols-3 md:items-end lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)_auto]">
          <div className="grid min-w-0 gap-2">
            <label
              htmlFor={nameId}
              className="text-xs font-semibold tracking-wide text-muted-foreground uppercase"
            >
              Name
            </label>
            <Input
              ref={nameInputRef}
              id={nameId}
              value={nameDraft}
              placeholder="Filter by display name"
              onChange={(event) => setNameDraft(event.target.value)}
            />
          </div>
          <FilterSelect
            label="Member state"
            value={rosterState}
            options={[
              ["active", "Active"],
              ["departed", "Departed"],
              ["all", "All"],
              ["present", "Present"],
              ["unknown", "Unknown"],
            ]}
            onChange={(next) =>
              updateFilters(
                { state: next as MemberRosterState },
                { replace: false },
              )
            }
          />
          <FilterSelect
            label="Title"
            value={title}
            options={[
              ["all", "All titles"],
              ["none", "No titles"],
              ...GUILD_RANK_CATALOG.map(
                ({ value, label }) =>
                  [`rank:${value}`, `${label} (rank)`] as [string, string],
              ),
              ...GUILD_SPECIAL_TITLE_CATALOG.map(
                ({ value, label }) =>
                  [`special:${value}`, `${label} (special)`] as [
                    string,
                    string,
                  ],
              ),
            ]}
            onChange={(next) =>
              updateFilters(
                {
                  title:
                    next === "all"
                      ? undefined
                      : (next as MembersSearch["title"]),
                },
                { replace: false },
              )
            }
          />
          {filtersActive &&
          !(
            matchesSelection &&
            query.data &&
            query.data.rows.length > 0 &&
            filteredRows.length === 0
          ) ? (
            <div className="md:col-span-3 md:justify-self-end lg:col-span-1 lg:justify-self-auto">
              <Button type="button" variant="outline" onClick={clearFilters}>
                Clear filters
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>
      {query.isLoading ? (
        <StatusAlert tone="info">Loading activity points...</StatusAlert>
      ) : matchesSelection && query.data ? (
        <>
          {query.isError ? (
            <StatusAlert tone="danger">
              Unable to refresh activity points. Showing saved data.
            </StatusAlert>
          ) : null}
          <ActivityPointsGrid
            result={query.data}
            filteredRows={filteredRows}
            filtersActive={filtersActive}
            onClearFilters={clearFilters}
            rosterState={rosterState}
            state={state}
            setState={(next) => {
              if (next.draft !== state.draft) {
                const entry = Object.entries(next.draft).find(
                  ([memberId, value]) => state.draft[memberId] !== value,
                )
                if (entry)
                  actions.updateRosterDraft(selectedPeriod, entry[0], entry[1])
              }
            }}
            onEdit={() => {
              originalPoints.current[selectedPeriod] = Object.fromEntries(
                query.data!.rows.map((row) => [
                  row.memberId,
                  row.points === null ? "" : String(row.points),
                ]),
              )
              actions.editRoster(selectedPeriod, query.data!.rows)
            }}
            onCancel={() => actions.cancelRoster(selectedPeriod)}
            onSave={() => void save(query.data!, state)}
            editButtonRef={(element) => {
              editButtonRefs.current[selectedPeriod] = element
            }}
            inputRef={(memberId, element) => {
              inputRefs.current[memberId] = element
            }}
          />
        </>
      ) : query.isError ? (
        <StatusAlert tone="danger">Unable to load activity points.</StatusAlert>
      ) : (
        <StatusAlert tone="info">Loading activity points...</StatusAlert>
      )}
    </div>
  )
}

function ActivityPointsGrid({
  result,
  filteredRows,
  filtersActive,
  onClearFilters,
  rosterState,
  state,
  setState,
  onEdit,
  onCancel,
  onSave,
  editButtonRef,
  inputRef,
}: {
  result: ActivityPointsMonth
  filteredRows: ActivityPointsRow[]
  filtersActive: boolean
  onClearFilters: () => void
  rosterState: MemberRosterState
  state: MembersRosterState
  setState: (state: MembersRosterState) => void
  onEdit: () => void
  onCancel: () => void
  onSave: () => void
  editButtonRef: (element: HTMLButtonElement | null) => void
  inputRef: (memberId: MemberId, element: HTMLInputElement | null) => void
}) {
  if (result.rows.length === 0 && state.mode !== "editing")
    return (
      <StatusAlert tone="info">
        No members have monthly points for this reporting month.
      </StatusAlert>
    )
  const editing = state.mode === "editing"
  const count = filtersActive
    ? `${filteredRows.length} of ${result.rows.length} ${result.rows.length === 1 ? "member" : "members"}`
    : `${result.rows.length} ${result.rows.length === 1 ? "member" : "members"}`

  return (
    <Card>
      <CardHeader className="items-center gap-4">
        <div>
          <CardTitle>Member overview</CardTitle>
          <p className="text-sm text-muted-foreground" aria-live="polite">
            {count}
          </p>
        </div>
        <CardAction>
          {editing ? (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                disabled={state.pending}
                onClick={onCancel}
              >
                Cancel
              </Button>
              <Button type="button" disabled={state.pending} onClick={onSave}>
                {state.pending ? "Saving..." : "Save"}
              </Button>
            </div>
          ) : (
            <Button type="button" ref={editButtonRef} onClick={onEdit}>
              Edit points
            </Button>
          )}
        </CardAction>
      </CardHeader>
      <CardContent className="space-y-4">
        {state.invalidMemberId ? (
          <StatusAlert tone="danger">
            <span id={VALIDATION_ERROR_ID}>
              Points must be whole numbers between 0 and 2147483647.
            </span>
          </StatusAlert>
        ) : null}
        {state.error ? (
          <StatusAlert tone="danger">
            Activity points were not saved.
          </StatusAlert>
        ) : null}

        {filteredRows.length === 0 ? (
          <EmptyState
            icon={<SearchX className="size-5" />}
            title="No matching members"
            description="No members match the current filters."
            actions={
              <Button type="button" variant="outline" onClick={onClearFilters}>
                Clear filters
              </Button>
            }
          />
        ) : (
          <Table density="compact" stickyHeader>
            <TableCaption className="sr-only">
              Activity points for {MONTH_NAMES[result.month - 1]} {result.year}
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead>Member</TableHead>
                <TableHead className="whitespace-nowrap">
                  Discord status
                </TableHead>
                <TableHead>Joined</TableHead>
                <TableHead>Titles</TableHead>
                <TableHead className="w-36 text-right">Points</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredRows.map((row) => {
                const invalid = state.invalidMemberId === row.memberId
                return (
                  <TableRow key={row.memberId}>
                    <TableCell className="whitespace-normal">
                      <Link
                        to="/members/$memberId"
                        params={{ memberId: row.memberId }}
                        search={{
                          month: formatPeriod(result.year, result.month),
                          state: rosterState,
                        }}
                        className="font-medium text-primary underline underline-offset-4 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                      >
                        {row.displayName}
                      </Link>
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {DISCORD_PRESENCE_LABEL[row.discordPresence]}
                    </TableCell>
                    <TableCell className="whitespace-nowrap">
                      {row.joinedAt
                        ? formatGuildJoinDate(row.joinedAt)
                        : "Unknown"}
                    </TableCell>
                    <TableCell className="whitespace-normal">
                      <div className="flex flex-wrap gap-2">
                        {row.rankTitle ? (
                          <span className="rounded-full bg-primary/10 px-3 py-1 text-sm font-medium whitespace-nowrap text-primary">
                            {GUILD_RANK_LABEL[row.rankTitle]}
                          </span>
                        ) : null}
                        {row.specialTitles.map((rowTitle) => (
                          <span
                            key={rowTitle}
                            className="rounded-full bg-muted px-3 py-1 text-sm whitespace-nowrap"
                          >
                            {GUILD_SPECIAL_TITLE_LABEL[rowTitle]}
                          </span>
                        ))}
                        {!row.rankTitle && row.specialTitles.length === 0 ? (
                          <span className="text-sm text-muted-foreground">
                            No titles
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell className="w-36 text-right">
                      {editing ? (
                        <Input
                          ref={(element) => inputRef(row.memberId, element)}
                          aria-label={`Points for ${row.displayName}`}
                          aria-invalid={invalid || undefined}
                          aria-describedby={
                            invalid ? VALIDATION_ERROR_ID : undefined
                          }
                          type="number"
                          min={0}
                          max={MAX_INTEGER}
                          step={1}
                          disabled={state.pending}
                          value={
                            state.draft[row.memberId] ??
                            (row.points === null ? "" : String(row.points))
                          }
                          onChange={(event) =>
                            setState({
                              ...state,
                              draft: {
                                ...state.draft,
                                [row.memberId]: event.target.value,
                              },
                              error: false,
                              invalidMemberId: null,
                            })
                          }
                        />
                      ) : row.points === null ? (
                        <>
                          <span aria-hidden="true">—</span>
                          <span className="sr-only">Not entered</span>
                        </>
                      ) : (
                        String(row.points)
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  )
}
